/**
@format
@flow local-strict
*/

import * as React from 'react';
import styled from 'styled-components';
import { Squircle as KrispySquircle } from '@krispy/squircles/web';
import Icon from '@ziynx/components/icons';
import Colors from '@ziynx/theme/colors';
import { Body, Heading, SubJumbo } from '@ziynx/components/typography';

type Props = $ReadOnly<{|
  /**
  * Children passed in
  */
  children?: React.Node,
  /**
   * Size of squircle
   */
  size?: 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge' | 'extraExtraLarge',
  /**
  * Inverts the background/shadow if true
  */
  inverted?: boolean,
  /**
  * Sets the icon as muted/disabled
  */
  disabled?: boolean,
  /**
  * Controls the border of the squircle shape
  */
  border?: boolean,
  /**
   * If squircle should not have shadow
   */
  noShadow?: boolean,
  /**
   * Color if inverted, optional
   * Default to PrimaryBlue
   */
  invertedColor?: string,
  /**
   * Background color if inverted
   */
  invertedBackgroundColor?: string,
  /**
  * Color of the border if set to true
  */
  borderColor?: string,
  /**
   * Name for icon
   */
  iconName?: string,
  /**
   * Color for icon or text
   */
  color?: string,
  /**
   * Text for withText option
   */
  text?: string,
  /**
   * Background Color
   */
  backgroundColor?: string,
  /**
   * Text for edit mode
   */
  overlayText?: string,
  /**
   * Active state made for extraSmall size
   * Adds PrimaryBlue background and white text/icon color
   */
  active?: boolean,
|}>;

/**
 * Formatting for edit text
 */
const StyledTextWrapper = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  opacity: 0.7;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 1px;
  background-color: ${Colors.GrayVeryDark};
`;

const StyledText = styled(Body)`
  text-align: center;
  opacity: 1.0;
`;

const StyledBody = styled(Body)`
  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 144dpi) {
    width: 16px;
    }
  }
`;

const sizeOptions = {
  extraSmall: { iconSize: 'mini', textType: 'body', textSize: 'extraSmall' },
  small: { iconSize: 'veryTiny', textType: 'body', textSize: 'small' },
  medium: { iconSize: 'tiny', textType: 'body', textSize: 'medium' },
  large: { iconSize: 'small', textType: 'heading', textSize: 'large' },
  extraLarge: { iconSize: 'medium', textType: 'subJumbo', textSize: 'medium' },
  extraExtraLarge: { iconSize: 'large', textType: 'subJumbo', textSize: 'large' },
};

/**
 * Squircle component extending Krispy's squircle
 * 3 children variations image, icon, and text
 * unless specifically provide custom children
 * Default withIcon
 */
const Squircle = (props: Props): React$Node => {
  const {
    size,
    inverted,
    disabled,
    noShadow,
    border,
    borderColor,
    invertedColor,
    invertedBackgroundColor,
    iconName,
    color,
    text,
    backgroundColor,
    overlayText,
    active,
    children,
    ...rest
  } = props;

  // to fix a flow warning
  const squircleSize: string = size || 'medium';
  const iconSize: string = sizeOptions[squircleSize]?.iconSize;
  const textSize: string = sizeOptions[squircleSize]?.textSize;
  const textType: string = sizeOptions[squircleSize]?.textType;

  const alphaNumeric = /^[a-z0-9]+$/i;

  const notText = !!text && !text?.match(alphaNumeric);

  const isChrome = !!window.chrome;

  const findColor = (iconOrText): string => {
    if (disabled) return Colors.White;
    if (inverted || active) return invertedColor || Colors.White;
    if (!inverted && iconOrText === 'icon') return color || Colors.PrimaryBlue;
    if (!inverted && iconOrText === 'text') return color || Colors.GrayDark;
    return Colors.GrayDark;
  };

  const findBackgroundColor = (): string => {
    if (disabled) return Colors.GrayLight;
    if (inverted) return invertedBackgroundColor || Colors.PrimaryBlue;
    if (active) return Colors.PrimaryBlue;
    if (backgroundColor) return backgroundColor;
    return Colors.White;
  };

  const renderWithIcon = (): React$Node => (
    <Icon
      name={iconName}
      {...{ [iconSize]: true }}
      color={findColor('icon')}
    />
  );

  const renderWithText = (): React$Node => {
    let TextComponent;
    switch (textType) {
    case 'heading':
      TextComponent = Heading;
      break;
    case 'subJumbo':
      TextComponent = SubJumbo;
      break;
    case 'body':
    default:
      TextComponent = Body;
      break;
    }

    if (textType === 'body' && size === 'medium' && isChrome && notText) {
      return (
        <StyledBody
          color={findColor('text')}
          medium
        >
          {text}
        </StyledBody>
      );
    }

    return (
      <TextComponent
        color={findColor('text')}
        {...{ [textSize]: true }}
      >
        {text || 'AN'}
      </TextComponent>
    );
  };

  const renderDefaultChildren = (): React$Node => {
    let variant;

    if (iconName && !text) {
      variant = 'withIcon';
    } else {
      variant = 'withText';
    }

    switch (variant) {
    case 'withText':
      return renderWithText();
    case 'withIcon':
    default:
      return renderWithIcon();
    }
  };

  const renderOverlay = (): React$Node => (
    <StyledTextWrapper>
      <StyledText
        extraSmall
        color={Colors.White}
      >
        {overlayText}
      </StyledText>
    </StyledTextWrapper>
  );

  return (
    <KrispySquircle
      extraSmall={size === 'extraSmall'}
      small={size === 'small'}
      medium={size === 'medium' || !size}
      large={size === 'large'}
      extraLarge={size === 'extraLarge'}
      extraExtraLarge={size === 'extraExtraLarge'}
      inverted={inverted}
      noShadow={noShadow}
      disabled={disabled}
      border={border}
      borderColor={borderColor || Colors.PrimaryBlue}
      colorConfig={Colors}
      backgroundColor={findBackgroundColor()}
      {...rest}
    >
      {!children && renderDefaultChildren()}
      {children}
      {overlayText && renderOverlay()}
    </KrispySquircle>
  );
};

Squircle.defaultProps = {
  inverted: false,
  disabled: false,
  border: false,
  borderColor: undefined,
  color: undefined,
  overlayText: undefined,
};

export default Squircle;
